import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import { Modal } from "@/shared/motion";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * RecoverPaidRideDialog — admin-only recovery form for the case where a
 * Stripe payment succeeded but the corresponding internal route never
 * activated (legacy clients with empty PI metadata, webhook outages, etc).
 *
 * Surfaces the existing POST /api/admin/ops/recover-paid-route endpoint
 * as a mobile-first form.  Validates client-side then submits; the
 * backend will upsert the rider, build the route, and generate trips.
 * The block generator then surfaces those trips as claimable blocks on
 * the driver marketplace.  No Stripe re-charge happens.
 *
 * Mode toggle: one-time (single ISO date) OR recurring (weekday list).
 * Backend rejects when both or neither are set, but we enforce it in
 * the UI too so the operator sees the constraint.
 */
export default function RecoverPaidRideDialog({ open, onClose, onSuccess }) {
  const [mode, setMode] = useState("onetime"); // "onetime" | "recurring"
  const [riderEmail, setRiderEmail] = useState("");
  const [riderName, setRiderName] = useState("");
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [oneTimeDate, setOneTimeDate] = useState("");
  const [days, setDays] = useState([]);
  const [arrival, setArrival] = useState("09:00");
  const [returnTime, setReturnTime] = useState("17:00");
  const [buffer, setBuffer] = useState("30");
  const [billingCycle, setBillingCycle] = useState("payg");
  const [piId, setPiId] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // backend response on success
  const [error, setError] = useState(null);

  // Reset on close so reopen = clean form (success preview kept inline so
  // operator can copy the route_id before dismissing).
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
  }, [open]);

  const toggleDay = (d) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const validate = () => {
    if (!riderEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(riderEmail)) return "Valid rider email is required.";
    if (!pickup.trim()) return "Pickup address is required.";
    if (!destination.trim()) return "Destination address is required.";
    const dMi = Number(distance);
    if (!(dMi > 0 && dMi <= 200)) return "Distance must be between 0.1 and 200 miles.";
    const dMin = Number(duration || 0);
    if (!(dMin >= 0 && dMin <= 600)) return "Duration must be 0–600 minutes.";
    if (mode === "onetime" && !oneTimeDate) return "Pick the ride date for a one-time recovery.";
    if (mode === "recurring" && days.length === 0) return "Select at least one weekday for a recurring recovery.";
    if (!/^\d{2}:\d{2}$/.test(arrival)) return "Arrival time must be HH:MM (24h).";
    if (!/^\d{2}:\d{2}$/.test(returnTime)) return "Return time must be HH:MM (24h).";
    const buf = Number(buffer);
    if (!(buf >= 0 && buf <= 60)) return "Buffer must be 0–60 minutes.";
    const amt = Number(amount);
    if (!(amt >= 0)) return "Amount paid must be ≥ $0.";
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        rider_email: riderEmail.trim().toLowerCase(),
        rider_name: riderName.trim() || null,
        pickup_address: pickup.trim(),
        destination_address: destination.trim(),
        distance_miles: Number(distance),
        duration_minutes: Number(duration || 0),
        arrival_time: arrival,
        return_time: returnTime,
        buffer_minutes: Number(buffer),
        billing_cycle: billingCycle,
        stripe_payment_intent_id: piId.trim() || null,
        amount_paid: Number(amount),
      };
      if (mode === "onetime") body.one_time_date = oneTimeDate;
      else body.days = days;

      const { data } = await api.post("/admin/ops/recover-paid-route", body);
      setResult(data);
      toast.success(`Recovered route · ${data.trips_inserted} trip(s) · ${data.trips_assigned} assigned`);
      onSuccess?.(data);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Recovery failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setMode("onetime");
    setRiderEmail(""); setRiderName("");
    setPickup(""); setDestination("");
    setDistance(""); setDuration("");
    setOneTimeDate(""); setDays([]);
    setArrival("09:00"); setReturnTime("17:00");
    setBuffer("30"); setBillingCycle("payg");
    setPiId(""); setAmount("");
    setResult(null); setError(null);
    onClose?.();
  };

  // Today (local) in YYYY-MM-DD for the date input min.
  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={closeAndReset}
      ariaLabel="Recover paid ride"
      testid="recover-dialog"
      className="bg-white sm:rounded-2xl border hairline shadow-2xl max-h-[92vh] overflow-y-auto sm:max-w-lg"
    >
        <header className="sticky top-0 bg-white border-b hairline px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="label-xs text-[#ed6500]">Ops · Recovery</div>
            <h2 className="font-serif text-lg sm:text-xl mt-0.5 truncate">Recover Paid Ride</h2>
          </div>
          <button
            onClick={closeAndReset}
            className="p-2 text-[#515E7B] hover:text-[#0A1128]"
            aria-label="Close"
            data-testid="recover-close"
          >
            <X size={18} />
          </button>
        </header>

        {result ? (
          <div className="p-5 sm:p-6 space-y-3" data-testid="recover-success">
            <div className="flex items-center gap-2 text-[#0A1128]">
              <ShieldCheck size={18} className="text-[#ed6500]" />
              <span className="font-serif text-base">Route recovered</span>
            </div>
            <dl className="text-sm space-y-1.5">
              <Row k="route_id" v={result.route_id} testId="recover-result-route" />
              <Row k="user_id" v={result.user_id} testId="recover-result-user" />
              <Row k="trips_inserted" v={String(result.trips_inserted)} testId="recover-result-trips" />
              <Row k="trips_assigned" v={String(result.trips_assigned)} testId="recover-result-assigned" />
              <Row k="tier" v={result.tier} testId="recover-result-tier" />
            </dl>
            <button
              onClick={closeAndReset}
              className="w-full mt-3 bg-[#0A1128] text-white py-3 text-[10px] tracking-widest uppercase font-semibold hover:bg-[#1a2348] transition-colors"
              data-testid="recover-done"
            >Done</button>
          </div>
        ) : (
          <div className="p-5 sm:p-6 space-y-5">
            <p className="text-xs text-[#515E7B] leading-relaxed">
              Use this when a Stripe charge succeeded but the rider's route never activated. The backend will upsert the user, build the route, and generate trips — the driver marketplace will then surface those legs as claimable blocks. <span className="text-[#ed6500] font-semibold">No re-charge happens.</span>
            </p>

            {/* Mode */}
            <Section label="Recovery mode">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "onetime", label: "One-time", sub: "Single date" },
                  { id: "recurring", label: "Recurring", sub: "Weekday list" },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMode(m.id)}
                    className={`text-left px-3 py-2.5 border transition-colors ${
                      mode === m.id ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-white text-[#0A1128] border-[#d8d4ce] hover:border-[#0A1128]"
                    }`}
                    data-testid={`recover-mode-${m.id}`}
                    data-selected={mode === m.id}
                  >
                    <div className="text-[10.5px] tracking-widest uppercase font-semibold">{m.label}</div>
                    <div className={`text-[10px] mt-0.5 ${mode === m.id ? "text-white/85" : "text-[#515E7B]"}`}>{m.sub}</div>
                  </button>
                ))}
              </div>
            </Section>

            {/* Rider */}
            <Section label="Rider">
              <Field label="Email" testId="recover-rider-email">
                <input
                  type="email"
                  value={riderEmail}
                  onChange={(e) => setRiderEmail(e.target.value)}
                  className="input-line w-full"
                  placeholder="rider@example.com"
                  data-testid="recover-rider-email-input"
                />
              </Field>
              <Field label="Full name (optional)" testId="recover-rider-name">
                <input
                  type="text"
                  value={riderName}
                  onChange={(e) => setRiderName(e.target.value)}
                  className="input-line w-full"
                  placeholder="Jane Smith"
                  data-testid="recover-rider-name-input"
                />
              </Field>
            </Section>

            {/* Route */}
            <Section label="Route">
              <Field label="Pickup address" testId="recover-pickup">
                <input
                  type="text"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  className="input-line w-full"
                  placeholder="123 Main St, Kansas City, MO"
                  data-testid="recover-pickup-input"
                />
              </Field>
              <Field label="Destination address" testId="recover-destination">
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="input-line w-full"
                  placeholder="900 W 47th St, Kansas City, MO"
                  data-testid="recover-destination-input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Distance (miles)" testId="recover-distance">
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="200"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    className="input-line w-full"
                    placeholder="8.4"
                    data-testid="recover-distance-input"
                  />
                </Field>
                <Field label="Duration (min)" testId="recover-duration">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="600"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="input-line w-full"
                    placeholder="22"
                    data-testid="recover-duration-input"
                  />
                </Field>
              </div>
            </Section>

            {/* Schedule */}
            <Section label="Schedule">
              {mode === "onetime" ? (
                <Field label="Ride date" testId="recover-onetime-date">
                  <input
                    type="date"
                    min={todayIso}
                    value={oneTimeDate}
                    onChange={(e) => setOneTimeDate(e.target.value)}
                    className="input-line w-full"
                    data-testid="recover-onetime-date-input"
                  />
                </Field>
              ) : (
                <div data-testid="recover-days">
                  <Label>Days of week</Label>
                  <div className="grid grid-cols-7 gap-1.5 mt-1.5">
                    {DAYS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        className={`py-2 text-[10px] tracking-widest uppercase font-semibold border transition-colors ${
                          days.includes(d) ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-white text-[#0A1128] border-[#d8d4ce] hover:border-[#0A1128]"
                        }`}
                        data-testid={`recover-day-${d}`}
                        data-selected={days.includes(d)}
                      >{d}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Arrival (HH:MM)" testId="recover-arrival">
                  <input
                    type="time"
                    value={arrival}
                    onChange={(e) => setArrival(e.target.value)}
                    className="input-line w-full"
                    data-testid="recover-arrival-input"
                  />
                </Field>
                <Field label="Return (HH:MM)" testId="recover-return">
                  <input
                    type="time"
                    value={returnTime}
                    onChange={(e) => setReturnTime(e.target.value)}
                    className="input-line w-full"
                    data-testid="recover-return-input"
                  />
                </Field>
              </div>
              <Field label="Buffer (min)" testId="recover-buffer">
                <input
                  type="number"
                  min="0"
                  max="60"
                  step="5"
                  value={buffer}
                  onChange={(e) => setBuffer(e.target.value)}
                  className="input-line w-full"
                  data-testid="recover-buffer-input"
                />
              </Field>
            </Section>

            {/* Payment */}
            <Section label="Payment record">
              <Field label="Stripe Payment Intent ID (optional)" testId="recover-pi">
                <input
                  type="text"
                  value={piId}
                  onChange={(e) => setPiId(e.target.value)}
                  className="input-line w-full"
                  placeholder="pi_3OabcdEfghIjkLMNop1234"
                  data-testid="recover-pi-input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount paid ($)" testId="recover-amount">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="input-line w-full"
                    placeholder="42.50"
                    data-testid="recover-amount-input"
                  />
                </Field>
                <Field label="Billing cycle" testId="recover-cycle">
                  <select
                    value={billingCycle}
                    onChange={(e) => setBillingCycle(e.target.value)}
                    className="input-line w-full bg-white"
                    data-testid="recover-cycle-input"
                  >
                    <option value="payg">payg (one-time)</option>
                    <option value="weekly">weekly</option>
                    <option value="biweekly">biweekly</option>
                    <option value="monthly">monthly</option>
                  </select>
                </Field>
              </div>
            </Section>

            {error && (
              <div
                className="border-l-2 border-[#ed6500] bg-[#fff4eb] p-3 text-xs text-[#0A1128] flex items-start gap-2"
                data-testid="recover-error"
                role="alert"
              >
                <AlertTriangle size={14} className="text-[#ed6500] shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-[#ed6500] text-white py-3.5 text-[10px] tracking-widest uppercase font-semibold hover:bg-[#d65a00] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="recover-submit"
            >
              {submitting ? (<><Loader2 size={14} className="animate-spin" /> Recovering…</>) : "Recover route"}
            </button>
          </div>
        )}
    </Modal>
  );
}

function Section({ label, children }) {
  return (
    <section className="space-y-2.5">
      <div className="label-xs text-[#515E7B]">{label}</div>
      {children}
    </section>
  );
}

function Label({ children }) {
  return <div className="text-[10px] tracking-widest uppercase font-semibold text-[#515E7B]">{children}</div>;
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Row({ k, v, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b hairline pb-1.5">
      <dt className="text-[10px] tracking-widest uppercase font-semibold text-[#515E7B]">{k}</dt>
      <dd className="text-xs text-[#0A1128] font-mono truncate" data-testid={testId}>{v}</dd>
    </div>
  );
}
